<template>
  <div
    data-component-group="business-form"
    data-component="notes"
  >
    <slot name="beforeForm" />

    <div
      v-if="!isDisabled"
      class="component-header"
    >
      <MagicContainer class="mb-2">
        <v-autocomplete
          data-cols="6"
          v-model="country"
          :items="threadSubmissions"
          prepend-inner-icon="public"
          label="Country"
          item-title="name"
          item-value="code"
          :return-object="true"
          required
          :readonly="isDisabled"
          :error-messages="countryErrors"
          @blur="v$.country.$touch()"
        />
      </MagicContainer>

      <base-enhanced-editor
        :value="note"
        :config="editorConfig"
        :disabled="isDisabled"
        :error-messages="noteErrors"
        @input="onChangeNote"
        @blur="onBlur"
      />

      <div class="component-actions">
        <v-btn
          color="secondary"
          :loading="isLoading"
          @click="onUpdate"
        >
          <v-icon>edit</v-icon>
          {{ triggerLabel }}
        </v-btn>
      </div>
    </div>

    <div>
      <Removables
        v-if="hasItems"
        :items="notes"
        :is-disabled="isDisabled"
        :on-remove="removeNote"
      >
        <template v-slot:item="scope">
          <div
            class="w-100"
            style="cursor: pointer"
            @click="setEditNote(scope.item)"
          >
            <div class="d-flex mb-1">
              <v-icon>public</v-icon>
              <span>{{ scope.item.country.name }}</span>
            </div>
            <div class="d-flex">
              <v-icon>label</v-icon>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span
                style="display: inline-block; width: calc(100% - 30px);"
                v-html="scope.item.note"
              ></span>
            </div>
          </div>
        </template>
      </Removables>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import Removables from '../../lists/Removables';
  import { isObj, isNum, isStr } from '../../../utils';
  import defaultEditorConfig from '../../../config/defaultEditorConfig';
  import { find } from 'lodash-es';
  // TODO: Translate component

  export default {
    components: {
      Removables
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        country: {
          required,
        },
        note: {
          required,
        },
      };
    },
    props: {
      business: {
        type: Object,
        default: undefined,
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        country: null,
        note: '',
        editNote: null,
        editorConfig: {
          ...defaultEditorConfig,
          placeholder: 'Type comment here...',
        },
        isLoading: false,
      };
    },
    computed: {
      userCountries() {
        return this.$store.getters['users/countries'];
      },
      threadSubmissions() {
        const { threadSubmissions } = this.$store.getters['businesses/activeItem'] || {}  ;

        return threadSubmissions?.filter((item) => {
          return find(this.userCountries, { code: item.code });
        });
      },
      notes() {
        const { notes } = this.$store.getters['businesses/activeItem'];
        return notes;
      },
      triggerLabel() {
        return this.isEditMode ? 'Update' : 'Attach';
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Country is required';
          }
          return error.$message;
        });
      },
      noteErrors() {
        return this.v$.note.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Note is required';
          }
          return error.$message;
        });
      },
      hasItems() {
        return this.notes.length > 0;
      },
      isEditMode() {
        return isNum(this.editNote?.id);
      },
    },
    watch: {
      business: {
        immediate: true,
        handler(nextBusiness, prevBusiness) {
          if (isObj(nextBusiness) && nextBusiness.id) {
            if (prevBusiness !== undefined) {
              this.clear();
            }
            this.getNotes(nextBusiness);
          }
        }
      },
    },
    methods: {
      onUpdate() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        const { country, note, editNote, isEditMode } = this;

        if (isEditMode) {
          return this.updateNote({
            country,
            note,
            note_id: editNote.id,
          });
        }

        this.addNote({
          country,
          note,
        });
      },
      canSubmitRequest() {
        const { country, note } = this;

        return isStr(country?.code) && note !== '';
      },
      getNotes({ id }) {
        const query = { id };

        return this.$store.dispatch('businesses/getNotes', query);
      },
      addNote({ country, note }) {
        const query = {
          id: this.business.id,
          country: country.code,
          note,
        };

        this.isLoading = true;
        return this.$store
          .dispatch('businesses/addNote', query)
          .then((res) => {
            this.clear();
            return res;
          })
          .catch(err => err)
          .finally(()=>{
            this.isLoading = false;
          });
      },
      updateNote({ country, note, note_id }) {
        const query = {
          id: this.business.id,
          country: country.code,
          note,
          note_id,
        };

        this.isLoading = true;
        return this.$store
          .dispatch('businesses/updateNote', query)
          .then((res) => {
            this.clear();
            return res;
          })
          .catch(err => err)
          .finally(()=>{
            this.isLoading = false;
          });
      },
      removeNote(note) {
        const query = {
          id: this.business.id,
          note_id: note.id,
        };

        return this.$store.dispatch('businesses/removeNote', query);
      },
      setEditNote(note) {
        if (this.isDisabled) {
          return false;
        }

        this.editNote = note;
        this.country = note.country;
        this.note = note.note;
      },
      clear() {
        this.v$.$reset();
        this.country = null;
        this.note = '';
        this.editNote = null;
      },
      onChangeNote(note) {
        this.note = note;
      },
      onBlur() {
        this.v$.country.$touch();
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='business-form'][data-component='notes'] {
  padding: $spacing-xl;
  background-color: map.get($colors, shades, 'white');
  border-top: 1px solid map.get($colors, grey, lighten-2);

  .component-actions {
    text-align: right;
    margin-bottom: 32px;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  .v-list--two-line .v-list-item {
    height: auto;
    align-items: flex-start;
  }

  .v-list-item__content {
    padding: 8px 0;
  }

  [data-component='amount'] {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  [data-component='removables'] {
    padding: 0;

    .v-list {
      padding: 0;
    }

    .v-list-item {
      height: auto;
      padding: 0 16px;
      border-radius: $border-radius;
      background-color: map.get($colors, shades, 'white');
    }

    .v-icon {
      margin-right: 8px;
    }
  }
}
</style>
